import React, { useMemo, useState } from 'react';

// Exact unit formatting (up to 2dp, trailing zeros stripped)
const cleanUnits = (n) => {
    const rounded = Math.round((Number(n) + Number.EPSILON) * 100) / 100;
    return rounded.toFixed(2).replace(/\.?0+$/, '');
};

const getTipperLabel = (bet) =>
    bet.tipper_discord_name || bet.tipper_email || (bet.user_id != null ? `User #${bet.user_id}` : 'Unknown');

// Medal accents for the top three ranks
const RANK_STYLES = {
    1: { ring: 'ring-amber-400/40', badge: 'bg-amber-400/15 text-amber-300 border-amber-400/30' },
    2: { ring: 'ring-slate-300/40', badge: 'bg-slate-300/15 text-slate-200 border-slate-300/30' },
    3: { ring: 'ring-orange-500/40', badge: 'bg-orange-500/15 text-orange-300 border-orange-500/30' },
};

function TipperBreakdown({ bets, unitSize = 1, titleSuffix = null }) {
    const [sortBy, setSortBy] = useState('pl');

    const tipperData = useMemo(() => {
        const grouped = {};

        bets.forEach(bet => {
            const id = bet.user_id != null ? String(bet.user_id) : 'unknown';
            const divisor = unitSize || 1;

            if (!grouped[id]) {
                grouped[id] = {
                    key: id,
                    name: getTipperLabel(bet),
                    bets: 0,
                    wins: 0,
                    losses: 0,
                    totalStaked: 0,
                    totalReturned: 0,
                    pl: 0
                };
            }

            grouped[id].bets++;
            grouped[id].totalStaked += bet.stake / divisor;
            grouped[id].pl += bet.pl / divisor;

            if (bet.result !== 'pending') {
                grouped[id].totalReturned += (bet.stake + bet.pl) / divisor;
            }
            if (bet.result === 'win') grouped[id].wins++;
            if (bet.result === 'loss') grouped[id].losses++;
        });

        return Object.values(grouped)
            .map(t => ({
                ...t,
                winRate: (t.wins + t.losses) > 0 ? (t.wins / (t.wins + t.losses)) * 100 : 0,
                roi: t.totalStaked > 0 ? (t.pl / t.totalStaked) * 100 : 0
            }))
            .sort((a, b) => {
                if (sortBy === 'roi') return b.roi - a.roi;
                if (sortBy === 'bets') return b.bets - a.bets;
                return b.pl - a.pl;
            });
    }, [bets, sortBy, unitSize]);

    const totalPL = tipperData.reduce((sum, t) => sum + t.pl, 0);

    const formatCurrency = (value) => `${value >= 0 ? '+' : '-'}${cleanUnits(Math.abs(value))}U`;
    const formatStaked = (value) => `${cleanUnits(value)}U`;

    if (tipperData.length === 0) return null;

    return (
        <div className="bg-[#0f172a] rounded border border-slate-700/50 overflow-hidden h-full">
            {/* Command Bar Header */}
            <div className="flex items-center justify-between px-3 md:px-4 py-2.5 border-b border-slate-700/50 bg-slate-800/30">
                <div className="flex items-center gap-2 md:gap-3 min-w-0">
                    <div className="w-1 h-4 bg-amber-400" />
                    <h3 className="text-xs md:text-sm font-semibold text-white tracking-tight whitespace-nowrap">Tipper Leaderboard</h3>
                    {titleSuffix && (
                        <span className="text-[10px] md:text-[11px] font-mono text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded px-1.5 py-0.5 whitespace-nowrap">{titleSuffix}</span>
                    )}
                    <span className="hidden md:inline text-[10px] font-mono text-slate-600 tracking-widest uppercase">{tipperData.length} tippers</span>
                </div>
                <div className={`text-base md:text-lg font-bold font-mono tabular-nums ${totalPL >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {formatCurrency(totalPL)}
                </div>
            </div>

            <div className="p-3 md:p-4">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                        <div className="w-1 h-1 rounded-full bg-amber-400" />
                        <div className="text-[10px] font-mono text-slate-500 tracking-widest uppercase">Ranked by {sortBy === 'roi' ? 'ROI' : sortBy === 'bets' ? 'tips' : 'P/L'}</div>
                    </div>
                    <div className="text-[9px] font-mono text-slate-600 tracking-widest uppercase">Click columns to sort</div>
                </div>

                <div className="-mx-3 md:-mx-4 overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-slate-800/40 border-y border-slate-700/40">
                            <tr>
                                {[
                                    { key: 'rank', label: '#', align: 'center', sortable: false, mobile: true },
                                    { key: 'name', label: 'Tipper', align: 'left', sortable: false, mobile: true },
                                    { key: 'bets', label: 'Tips', align: 'right', sortable: true, mobile: true },
                                    { key: 'record', label: 'W · L', align: 'center', sortable: false, mobile: false },
                                    { key: 'winRate', label: 'Win %', align: 'left', sortable: false, mobile: false },
                                    { key: 'staked', label: 'Staked', align: 'right', sortable: false, mobile: false },
                                    { key: 'roi', label: 'ROI', align: 'right', sortable: true, mobile: true },
                                    { key: 'pl', label: 'P/L', align: 'right', sortable: true, mobile: true },
                                ].map(col => {
                                    const isActive = sortBy === col.key;
                                    const alignCls = col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left';
                                    const visibilityCls = col.mobile ? '' : 'hidden md:table-cell';
                                    const baseCls = `px-1.5 md:px-2 py-1.5 text-[9px] font-mono font-semibold tracking-widest uppercase ${alignCls} ${visibilityCls}`;
                                    return col.sortable ? (
                                        <th key={col.key} onClick={() => setSortBy(col.key)} className={`${baseCls} cursor-pointer transition-colors ${isActive ? 'text-emerald-400' : 'text-slate-500 hover:text-slate-300'}`}>
                                            <span className="inline-flex items-center gap-1">
                                                {col.label}
                                                {isActive && <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 12 12"><path d="M6 8L2 4h8z" /></svg>}
                                            </span>
                                        </th>
                                    ) : (
                                        <th key={col.key} className={`${baseCls} text-slate-500`}>{col.label}</th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700/30">
                            {tipperData.map((tipper, index) => {
                                const rank = index + 1;
                                const rankStyle = RANK_STYLES[rank];
                                const winRateClamped = Math.min(Math.max(tipper.winRate, 0), 100);
                                return (
                                    <tr key={tipper.key} className="hover:bg-slate-800/40 transition-colors">
                                        <td className="px-1.5 md:px-2 py-2 text-center">
                                            <span className={`inline-flex items-center justify-center w-5 h-5 rounded text-[10px] font-mono font-bold border ${rankStyle ? rankStyle.badge : 'bg-slate-700/40 text-slate-400 border-slate-600/40'}`}>
                                                {rank}
                                            </span>
                                        </td>
                                        <td className="px-1.5 md:px-2 py-2">
                                            <div className="flex items-center gap-1.5 md:gap-2 min-w-0 max-w-[160px]">
                                                <span className={`w-6 h-6 flex-shrink-0 rounded-full bg-slate-700/60 text-slate-200 text-[10px] font-bold flex items-center justify-center uppercase ring-2 ${rankStyle ? rankStyle.ring : 'ring-transparent'}`}>
                                                    {tipper.name.charAt(0)}
                                                </span>
                                                <span className="text-[11px] text-white font-medium truncate" title={tipper.name}>{tipper.name}</span>
                                            </div>
                                        </td>
                                        <td className="px-1.5 md:px-2 py-2 text-right text-[11px] font-mono text-slate-300 tabular-nums">{tipper.bets}</td>
                                        <td className="hidden md:table-cell px-2 py-2 text-center text-[11px] font-mono tabular-nums">
                                            <span className="text-emerald-400">{tipper.wins}</span>
                                            <span className="text-slate-600 mx-0.5">·</span>
                                            <span className="text-red-400">{tipper.losses}</span>
                                        </td>
                                        <td className="hidden md:table-cell px-2 py-2">
                                            <div className="flex items-center gap-1.5 min-w-[90px]">
                                                <div className="flex-1 h-1 bg-slate-700/50 rounded-sm overflow-hidden">
                                                    <div className={`h-full ${winRateClamped >= 50 ? 'bg-emerald-500/70' : 'bg-amber-500/70'}`} style={{ width: `${winRateClamped}%` }} />
                                                </div>
                                                <span className="text-[10px] font-mono text-slate-300 tabular-nums w-9 text-right">{tipper.winRate.toFixed(1)}%</span>
                                            </div>
                                        </td>
                                        <td className="hidden md:table-cell px-2 py-2 text-right text-[10px] font-mono text-slate-400 tabular-nums">{formatStaked(tipper.totalStaked)}</td>
                                        <td className="px-1.5 md:px-2 py-2 text-right">
                                            <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-mono font-semibold tabular-nums ${tipper.roi >= 0 ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
                                                {tipper.roi >= 0 ? '+' : ''}{tipper.roi.toFixed(1)}%
                                            </span>
                                        </td>
                                        <td className={`px-1.5 md:px-2 py-2 text-right text-[11px] md:text-[12px] font-mono font-bold tabular-nums whitespace-nowrap ${tipper.pl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                            {formatCurrency(tipper.pl)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default TipperBreakdown;
